import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Filter, TrendingUp, Users, Building, Code, FileText, XCircle } from 'lucide-react';
import { FaDollarSign, FaUserTie } from "react-icons/fa";
import { getSubmissionsByUserForDashboard, fetchExecutives, getUsersWithZeroSubmissions, getConsultantsWithZeroSubmissions } from '../../../Services/salesapis';
import Loader from '../../../components/Loader';
import MuiDatePicker from '../../../components/MuiDatePicker';
import moment from 'moment';
import CustomDropdown from '../../../components/CustomDropdown';
import { toast } from 'react-toastify';
import { getValuesFromLocalstorage } from '../../../components/Helpers';
import LOCAL_KEYS from '../../../helpers/localstorage';

const PieChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;
  const radius = 80;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative w-full h-64 max-w-64 mx-auto">
      <svg width="100%" height="100%" viewBox="0 0 256 256" className="transform -rotate-90">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
          const strokeDashoffset = -((cumulativePercentage / 100) * circumference);
          cumulativePercentage += percentage;
          
          return (
            <circle
              key={index}
              cx="128"
              cy="128"
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth="32"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out hover:stroke-width-36"
              style={{ transformOrigin: '128px 128px' }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-gray-800">{total}</div>
          <div className="text-xs sm:text-sm text-gray-600">Total</div>
        </div>
      </div>
    </div>
  );
};

const BarChart = ({ data }) => {
  const maxValue = Math.max(...data.map(item => item.value));
  const allZero = data.every(item => item.value === 0);

  return (
    <div className="space-y-4 w-full">
      {data.map((item, index) => {
        const labelLength = String(item.value).length;
        const minLabelPx = 18 + labelLength * 8;
        const containerPx = 300;
        const minBarPercent = Math.min(100, (minLabelPx / containerPx) * 100);
        let widthPercent = allZero
          ? minBarPercent
          : maxValue === 0
            ? minBarPercent
            : Math.max((item.value / maxValue) * 100, minBarPercent);
        return (
          <div key={index} className="flex items-center space-x-4 w-full">
            <div className="w-32 sm:w-40 md:w-48 text-sm font-medium text-gray-700 flex-shrink-0 truncate">{item.label}</div>
            <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden min-w-0 flex items-center">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                style={{
                  backgroundColor: item.color,
                  width: `${widthPercent}%`,
                  minWidth: `${minBarPercent}%`,
                  animationDelay: `${index * 200}ms`
                }}
              >
                <span className="text-white text-xs font-semibold">{item.value}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminDashboard = () => {
  // Dashboard filters (for cards and charts)
  const [dashboardSubmittedByFilter, setDashboardSubmittedByFilter] = useState('');
  const [dashboardTimeframeFilter, setDashboardTimeframeFilter] = useState('thisweek');
  const [dashboardStartDate, setDashboardStartDate] = useState('');
  const [dashboardEndDate, setDashboardEndDate] = useState('');
  const [dashboardData, setDashboardData] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  
  const [executives, setExecutives] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(true);

  // Zero submissions state
  const [showZeroUsers, setShowZeroUsers] = useState(false);
  const [zeroUsers, setZeroUsers] = useState([]);
  const [zeroUsersLoading, setZeroUsersLoading] = useState(false);
  const [zeroUsersError, setZeroUsersError] = useState(null);

  // Zero submissions for consultants
  const [showZeroConsultants, setShowZeroConsultants] = useState(false);
  const [zeroConsultants, setZeroConsultants] = useState([]);
  const [zeroConsultantsLoading, setZeroConsultantsLoading] = useState(false);
  const [zeroConsultantsError, setZeroConsultantsError] = useState(null);

  const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
  const isAdmin = userDetails?.acctType === 'ADMIN';
  const userFullName = `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim();

  const fetchGuardRef = useRef(false); // Prevent double API call
  const [toastShown, setToastShown] = useState(false); // Prevent duplicate toast

  useEffect(() => {
    if (!isAdmin && userFullName) {
      setDashboardSubmittedByFilter(userFullName);
    }
  }, [isAdmin, userFullName]);

  const fetchExecutivesData = async () => {
    try {
      setExecutivesLoading(true);
      const executivesData = await fetchExecutives();
      setExecutives(executivesData || []);
    } catch (err) {
      setExecutives([]);
    } finally {
      setExecutivesLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    if (fetchGuardRef.current) return; // Guard against double call
    fetchGuardRef.current = true;
    try {
      setDashboardLoading(true);
      let timeframe = null;
      let fromDate = null;
      let toDate = null;
      if (dashboardTimeframeFilter === 'thisweek') {
        timeframe = 'thisweek';
      } else if (dashboardTimeframeFilter === 'thismonth') {
        timeframe = 'thismonth';
      } else if (dashboardTimeframeFilter === 'custom') {
        if (dashboardStartDate && dashboardEndDate) {
          fromDate = dashboardStartDate;
          toDate = dashboardEndDate;
        } else {
          setDashboardLoading(false);
          fetchGuardRef.current = false;
          return;
        }
      }
      const submittedByParam = isAdmin ? dashboardSubmittedByFilter : userFullName;
      const response = await getSubmissionsByUserForDashboard(timeframe, fromDate, toDate, submittedByParam);
      const dataArray = response.content || response || [];
      setDashboardData(Array.isArray(dataArray) ? dataArray : []);
      setToastShown(false); // Reset toast flag on success
    } catch (err) {
      if (!toastShown) {
        toast.dismiss(); // Dismiss any existing toasts
        toast.error(err.message || 'Failed to fetch dashboard data');
        setToastShown(true);
      }
      setDashboardData([]);
    } finally {
      setDashboardLoading(false);
      setTimeout(() => { fetchGuardRef.current = false; }, 0); // Allow next call in next tick
    }
  };

  useEffect(() => {
    fetchExecutivesData();
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line
  }, [dashboardTimeframeFilter, dashboardStartDate, dashboardEndDate, dashboardSubmittedByFilter]);

  const chartData = useMemo(() => {
    const submittedByCount = dashboardData.reduce((acc, item) => {
      acc[item.submittedBy] = (acc[item.submittedBy] || 0) + 1;
      return acc;
    }, {});

    const consultantCount = dashboardData.reduce((acc, item) => {
      const name = item.consultantName || '';
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    const statusCount = dashboardData.reduce((acc, item) => {
      const status = item.stsCd === 'A' ? 'Active' : 'Closed';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const colors = ['#2563EB', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];

    const submittedByArr = Object.entries(submittedByCount)
      .map(([label, value], index) => ({
        label,
        value,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value);

    const consultantArr = Object.entries(consultantCount)
      .map(([label, value], index) => ({
        label,
        value,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value);

    const statusArr = Object.entries(statusCount).map(([label, value], index) => ({
      label,
      value,
      color: label === 'Active' ? '#2563EB' : '#7C3AED'
    }));

    return {
      submittedBy: submittedByArr,
      consultant: consultantArr,
      status: statusArr
    };
  }, [dashboardData]);

  const fetchZeroUsers = async () => {
    setZeroUsersLoading(true);
    setZeroUsersError(null);
    try {
      const users = await getUsersWithZeroSubmissions();
      setZeroUsers(users || []);
    } catch (err) {
      setZeroUsersError(err.message || 'No users found with zero submissions');
      setZeroUsers([]);
    } finally {
      setZeroUsersLoading(false);
    }
  };

  const handleToggleZeroUsers = () => {
    if (!showZeroUsers && zeroUsers.length === 0) {
      fetchZeroUsers();
    }
    setShowZeroUsers(v => !v);
  };

  const fetchZeroConsultants = async () => {
    setZeroConsultantsLoading(true);
    setZeroConsultantsError(null);
    try {
      const consultants = await getConsultantsWithZeroSubmissions();
      setZeroConsultants(consultants || []);
    } catch (err) {
      setZeroConsultantsError(err.message || 'No consultants found with zero submissions');
      setZeroConsultants([]);
    } finally {
      setZeroConsultantsLoading(false);
    }
  };

  const handleToggleZeroConsultants = () => {
    if (!showZeroConsultants && zeroConsultants.length === 0) {
      fetchZeroConsultants();
    }
    setShowZeroConsultants(v => !v);
  };

  const handleDashboardTimeframeChange = (newTimeframe) => {
    setDashboardTimeframeFilter(newTimeframe);
    if (newTimeframe !== 'custom') {
      setDashboardStartDate('');
      setDashboardEndDate('');
    }
    setShowZeroUsers(false);
    setShowZeroConsultants(false);
    setDashboardLoading(true);
  };

  const handleDashboardSubmittedByChange = (newSubmittedBy) => {
    setDashboardSubmittedByFilter(newSubmittedBy);
    setShowZeroUsers(false);
    setShowZeroConsultants(false);
    setToastShown(false); // Reset toast flag on person change
  };

  if (dashboardLoading) {
    return <Loader isLoading={true} />;
  }

  return (
    <div className="bg-gray-50">
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-3 sm:p-4 mb-4">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
      </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              {isAdmin ? (
                <CustomDropdown
                  value={executives.find(e => (e.name || e.fullName || e.userName || e) === dashboardSubmittedByFilter) ? { label: dashboardSubmittedByFilter || 'All Users', value: dashboardSubmittedByFilter } : { label: 'All Users', value: '' }}
                  name="submittedBy"
                  handleChange={option => handleDashboardSubmittedByChange(option.value)}
                  placeholder="Submitted By"
                  options={[
                    { label: 'All Users', value: '' },
                    ...executives.map(e => ({
                      label: e.name || e.fullName || e.userName || e,
                      value: e.name || e.fullName || e.userName || e
                    }))
                  ]}
                  handleGetOptionValue={opt => opt.value}
                  handleGetOptionName={opt => opt.label}
                  isDisabled={executivesLoading}
                  customClasses="w-full"
                  legend={true}
                />
              ) : (
                <CustomDropdown
                  value={{ label: userFullName, value: userFullName }}
                  name="submittedBy"
                  handleChange={() => {}}
                  placeholder="Submitted By"
                  options={[{ label: userFullName, value: userFullName }]}
                  handleGetOptionValue={opt => opt.value}
                  handleGetOptionName={opt => opt.label}
                  isDisabled={true}
                  customClasses="w-full opacity-60 cursor-not-allowed"
                  hideDropdownIndicator={true}
                  legend={true}
                />
              )}
        </div>

            <div>
              <CustomDropdown
                value={{ label: dashboardTimeframeFilter === 'thisweek' ? 'This Week' : dashboardTimeframeFilter === 'thismonth' ? 'This Month' : 'Custom Date Range', value: dashboardTimeframeFilter }}
                name="timeframe"
                handleChange={option => handleDashboardTimeframeChange(option.value)}
                placeholder="Timeframe"
                options={[
                  { label: 'This Week', value: 'thisweek' },
                  { label: 'This Month', value: 'thismonth' },
                  { label: 'Custom Date Range', value: 'custom' }
                ]}
                handleGetOptionValue={opt => opt.value}
                handleGetOptionName={opt => opt.label}
                customClasses="w-full"
                legend={true}
              />
            </div>
            
            {dashboardTimeframeFilter === 'custom' && (
              <>
                <div>
                  <MuiDatePicker
                    label="From Date"
                    value={dashboardStartDate ? moment(dashboardStartDate) : null}
                    onChange={date => setDashboardStartDate(date ? date.format('YYYY-MM-DD') : '')}
                    maxDate={dashboardEndDate ? moment(dashboardEndDate) : null}
                  />
                </div>
                <div>
                  <MuiDatePicker
                    label="To Date"
                    value={dashboardEndDate ? moment(dashboardEndDate) : null}
                    onChange={date => setDashboardEndDate(date ? date.format('YYYY-MM-DD') : '')}
                    minDate={dashboardStartDate ? moment(dashboardStartDate) : null}
                  />
            </div>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards and Charts without the extra scroll container */}
        {dashboardData.length > 0 && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
              {/* Total Submissions Card */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-3 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-blue-100 text-sm">Total Submissions</p>
                    <p className="text-2xl font-bold truncate">{dashboardData.length}</p>
                    <p className="text-blue-200 text-xs mt-1 line-clamp-1">
                      {dashboardTimeframeFilter === 'thisweek' && 'This Week(Monday to Today)'}
                      {dashboardTimeframeFilter === 'thismonth' && 'This Month(1st to Today)'}
                      {dashboardTimeframeFilter === 'custom' && dashboardStartDate && dashboardEndDate &&
                        `${moment(dashboardStartDate).format('MM/DD/YYYY')} - ${moment(dashboardEndDate).format('MM/DD/YYYY')}`
                      }
                    </p>
                  </div>
                  <FileText className="w-10 h-10 text-blue-200 flex-shrink-0 ml-2" />
                </div>
              </div>
              
              {/* Active Status Card */}
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-3 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-purple-100 text-sm">Active Status</p>
                    <p className="text-2xl font-bold truncate">{dashboardData.filter(item => item.stsCd === 'A').length}</p>
                    <p className="text-purple-200 text-xs mt-1 line-clamp-1">
                      {dashboardTimeframeFilter === 'thisweek' && 'This Week(Monday to Today)'}
                      {dashboardTimeframeFilter === 'thismonth' && 'This Month(1st to Today)'}
                      {dashboardTimeframeFilter === 'custom' && dashboardStartDate && dashboardEndDate &&
                        `${moment(dashboardStartDate).format('MM/DD/YYYY')} - ${moment(dashboardEndDate).format('MM/DD/YYYY')}`
                      }
                    </p>
            </div>
                  <TrendingUp className="w-10 h-10 text-purple-200 flex-shrink-0 ml-2" />
          </div>
        </div>

              {/* Closed Status Card */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-3 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-red-100 text-sm">Closed Status</p>
                    <p className="text-2xl font-bold truncate">{dashboardData.filter(item => item.stsCd === 'D').length}</p>
                    <p className="text-red-200 text-xs mt-1 line-clamp-1">
                      {dashboardTimeframeFilter === 'thisweek' && 'This Week(Monday to Today)'}
                      {dashboardTimeframeFilter === 'thismonth' && 'This Month(1st to Today)'}
                      {dashboardTimeframeFilter === 'custom' && dashboardStartDate && dashboardEndDate &&
                        `${moment(dashboardStartDate).format('MM/DD/YYYY')} - ${moment(dashboardEndDate).format('MM/DD/YYYY')}`
                      }
                    </p>
              </div>
                  <XCircle className="w-10 h-10 text-red-200 flex-shrink-0 ml-2" />
        </div>
      </div>

              {/* Consultants Marketed Card */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-3 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-orange-100 text-sm">Consultants Marketed</p>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold">{new Set(dashboardData.map(item => item.consultantName || '')).size}</span>
          </div>
                    <p className="text-orange-200 text-xs mt-1 line-clamp-1">
                      {dashboardTimeframeFilter === 'thisweek' && 'This Week(Monday to Today)'}
                      {dashboardTimeframeFilter === 'thismonth' && 'This Month(1st to Today)'}
                      {dashboardTimeframeFilter === 'custom' && dashboardStartDate && dashboardEndDate &&
                        `${moment(dashboardStartDate).format('MM/DD/YYYY')} - ${moment(dashboardEndDate).format('MM/DD/YYYY')}`
                      }
                    </p>
                  </div>
                  <FaUserTie className="w-10 h-10 text-orange-200 flex-shrink-0 ml-2" />
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {isAdmin && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Submissions by User</h3>
                    <button
                      className="text-blue-600 hover:underline text-sm font-medium focus:outline-none"
                      onClick={handleToggleZeroUsers}
                    >
                      {showZeroUsers ? 'Hide users with 0 submissions' : 'Show users with 0 submissions'}
                    </button>
                  </div>
                  {showZeroUsers ? (
                    zeroUsersLoading ? (
                      <Loader isLoading={true} />
                    ) : zeroUsersError ? (
                      <div className="text-red-500 text-sm">{zeroUsersError}</div>
                    ) : zeroUsers.length === 0 ? (
                      <div className="text-gray-500 text-sm">No users with zero submissions.</div>
                    ) : (
                      <div className="w-full" style={{ height: '250px', overflowY: 'auto' }}>
                        <BarChart data={zeroUsers.map(name => ({ label: name, value: 0, color: '#D1D5DB' }))} />
                      </div>
                    )
                  ) : (
                    chartData.submittedBy.length > 0 ? (
                      <div className="w-full" style={{ height: '250px', overflowY: 'auto' }}>
                        <BarChart data={chartData.submittedBy} />
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">No data available</div>
                    )
                  )}
                </div>
              )}
              
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 mb-0">Submissions for Consultant</h3>
                  <button
                    className="text-blue-600 hover:underline text-sm font-medium focus:outline-none"
                    onClick={handleToggleZeroConsultants}
                  >
                    {showZeroConsultants ? 'Hide consultants with 0 submissions' : 'Show consultants with 0 submissions'}
                  </button>
                </div>
                {showZeroConsultants ? (
                  zeroConsultantsLoading ? (
                    <Loader isLoading={true} />
                  ) : zeroConsultantsError ? (
                    <div className="text-red-500 text-sm">{zeroConsultantsError}</div>
                  ) : zeroConsultants.length === 0 ? (
                    <div className="text-gray-500 text-sm">No consultants with zero submissions.</div>
                  ) : (
                    <div className="w-full" style={{ height: '250px', overflowY: 'auto' }}>
                      <BarChart data={zeroConsultants.map(name => ({ label: name, value: 0, color: '#D1D5DB' }))} />
                    </div>
                  )
                ) : (
                  chartData.consultant.length > 0 ? (
                    <div className="w-full" style={{ height: '250px', overflowY: 'auto' }}>
                      <BarChart data={chartData.consultant} />
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">No data available</div>
                  )
                )}
              </div>
            </div>
          </>
        )}

        {/* Show message when no dashboard data */}
        {dashboardData.length === 0 && !dashboardLoading && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 text-center">
            <div className="text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-sm text-gray-500">
                No submissions found for the selected timeframe. Try selecting a different time period or user.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
